import { cn } from '../lib/cn';

interface Props {
  /** 1-based current page. */
  page: number;
  /** Total number of pages (>= 1). */
  pageCount: number;
  /** Total number of items across all pages. */
  total: number;
  /** Items per page. */
  pageSize: number;
  onChange: (page: number) => void;
  /** Singular noun for the count line, e.g. "order" → "12 orders". */
  unit?: string;
  className?: string;
}

// Builds a compact page list with ellipses: 1 … 4 [5] 6 … 20
function pageItems(page: number, pageCount: number): (number | '…')[] {
  const out: (number | '…')[] = [];
  const window = 1; // pages either side of the current one
  for (let n = 1; n <= pageCount; n++) {
    const edge = n === 1 || n === pageCount;
    const near = n >= page - window && n <= page + window;
    if (edge || near) out.push(n);
    else if (out[out.length - 1] !== '…') out.push('…');
  }
  return out;
}

/**
 * Footer pagination bar. Shows "Showing X–Y of Z" plus prev / numbered / next
 * controls. Renders the count line whenever there are items, and the nav only
 * when there's more than one page. Designed to sit at the bottom of a table
 * Card (it carries its own top border).
 */
export function Pagination({
  page,
  pageCount,
  total,
  pageSize,
  onChange,
  unit = 'result',
  className,
}: Props): JSX.Element | null {
  if (total === 0) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  const items = pageItems(page, pageCount);

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4 px-6 py-3 border-t border-rule-2 flex-wrap',
        className,
      )}
    >
      <span className="text-xs text-ink-3">
        Showing{' '}
        <span className="text-ink-2 kc-tnum">
          {from}–{to}
        </span>{' '}
        of <span className="text-ink-2 kc-tnum">{total}</span> {unit}
        {total === 1 ? '' : 's'}
      </span>
      {pageCount > 1 && (
        <div className="flex items-center gap-1">
          <PageBtn
            disabled={page <= 1}
            onClick={() => onChange(page - 1)}
            ariaLabel="Previous page"
          >
            ‹
          </PageBtn>
          {items.map((it, i) =>
            it === '…' ? (
              <span key={`gap-${i}`} className="px-1.5 text-ink-4 text-xs select-none">
                …
              </span>
            ) : (
              <PageBtn key={it} active={it === page} onClick={() => onChange(it)}>
                {it}
              </PageBtn>
            ),
          )}
          <PageBtn
            disabled={page >= pageCount}
            onClick={() => onChange(page + 1)}
            ariaLabel="Next page"
          >
            ›
          </PageBtn>
        </div>
      )}
    </div>
  );
}

function PageBtn({
  active,
  disabled,
  onClick,
  ariaLabel,
  children,
}: {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  ariaLabel?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'min-w-[32px] h-8 px-2 rounded text-xs font-medium border transition kc-tnum',
        active
          ? 'bg-accent text-white border-accent'
          : 'bg-surface text-ink-2 border-rule hover:border-accent/40 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-rule',
      )}
    >
      {children}
    </button>
  );
}
